import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import clsx from 'clsx';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Collapse from '@mui/material/Collapse';
import { useHistory } from 'react-router-dom';
import ExpandMore from '../../../assets/icons/svg/flaticons/fi-ss-angle-down.svg';
import ListItem from '@mui/material/ListItem';
import Icon from '@mui/material/Icon';
import { useSelector } from 'react-redux';
import { styled } from '@mui/system';
import * as MuiIcons from '@mui/icons-material';

import './SCUIMainMenu.scss';

// Define ArrowIcon outside
const StyledArrowIcon = styled('div')<{ open: boolean }>(({ open }) => ({
    transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: open ? 'rotate(0deg)' : 'rotate(270deg)',
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    color: '#c0bdbd',
    overflow: 'hidden',
    '& img': {
        width: '10px !important',
        height: '10px !important',
        maxWidth: '10px !important',
        maxHeight: '10px !important',
    },
}));

const ArrowIcon = memo(({ open, children }: { open: boolean; children: React.ReactNode }) => (
    <StyledArrowIcon open={open}>{children}</StyledArrowIcon>
));

interface IProps {
    id: string;
    onClickNestedMenu: any;
    key?: any;
    classNameFlatImg?: any;
    menuContent?: any;
    isCollapsed?: boolean;
    isOpen?: boolean;
}

// Memoize style objects to prevent recreation on resize
const mainMenuListStyle = { overflowX: 'hidden', height: '100%' } as const;

const listItemButtonStyles = {
    color: '#8a8a8a',
    '&:hover': {
        color: '#5189eb',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        '& .MuiListItemIcon-root': { color: '#5189eb' },
        '& .MuiListItemText-root': { color: '#5189eb' },
    },
} as const;

const selectedListItemButtonStyles = {
    color: '#5189eb',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
    '&:hover': {
        color: '#5189eb',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        '& .MuiListItemIcon-root': { color: '#5189eb' },
        '& .MuiListItemText-root': { color: '#5189eb' },
    },
} as const;

const listItemIconStyles = {
    paddingLeft: '2rem',
    color: '#8a8a8a',
} as const;

const selectedListItemIconStyles = {
    paddingLeft: '2rem',
    color: '#5189eb',
} as const;

const listItemTextStyles = {
    fontSize: '1.2rem',
    fontWeight: 'normal',
    color: '#8a8a8a',
    whiteSpace: 'break-spaces',
} as const;

const selectedListItemTextStyles = {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#5189eb',
} as const;

const subListItemStyles = {
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
} as const;

// Memoized menu item component
const MenuItem = memo(
    ({
        menuItem,
        index,
        selectedIndex,
        isOpen,
        onItemClick,
        onNavigate,
    }: {
        menuItem: any;
        index: number;
        selectedIndex: number;
        isOpen: boolean;
        onItemClick: (index: number) => void;
        onNavigate: (url: string) => void;
    }) => {
        const hasSubItems = menuItem.items?.menuItem?.length > 0;
        const isSelected = index === selectedIndex;

        // Memoize class names
        const subItemClassName = useMemo(
            () =>
                clsx({
                    'nestHamburgerMenuList listItem': !isOpen,
                    'nestedMenuList listItem': isOpen,
                }),
            [isOpen]
        );

        const renderIcon = (iconName: string) => {
            if (!iconName) return <Icon>star</Icon>;

            if (iconName.startsWith('mui_')) {
                const muiIconName = iconName.replace('mui_', '');
                const pascalCaseName = muiIconName
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join('');

                // @ts-expect-error - Dynamic component access
                const MuiIcon = MuiIcons[pascalCaseName];
                return MuiIcon ? <MuiIcon fontSize="small" /> : <Icon>star</Icon>;
            }
            return <Icon>{iconName}</Icon>;
        };

        return (
            <List style={{ padding: 0 }}>
                <Tooltip title={menuItem.text} placement="right">
                    <ListItemButton
                        onClick={() => onItemClick(index)}
                        onMouseDown={(e) => {
                            if (e.button === 1) {
                                // Middle click
                                e.preventDefault();
                                if (menuItem.navigateViewUrl) {
                                    window.open(menuItem.navigateViewUrl, '_blank', 'noopener,noreferrer');
                                }
                            }
                        }}
                        sx={isSelected ? selectedListItemButtonStyles : listItemButtonStyles}
                    >
                        {isOpen ? (
                            <>
                                <ListItemIcon sx={isSelected ? selectedListItemIconStyles : listItemIconStyles}>
                                    {renderIcon(menuItem.iconName)}
                                </ListItemIcon>
                                <ListItemText
                                    primary={menuItem.text}
                                    sx={isSelected ? selectedListItemTextStyles : listItemTextStyles}
                                    className="menuFont"
                                />
                                {hasSubItems && (
                                    <ArrowIcon open={isSelected}>
                                        <img src={ExpandMore} alt="expand more" />
                                    </ArrowIcon>
                                )}
                            </>
                        ) : (
                            <>
                                <Tooltip title={menuItem.text} placement="right">
                                    <ListItemIcon sx={isSelected ? selectedListItemIconStyles : listItemIconStyles}>
                                        {renderIcon(menuItem.iconName)}
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText
                                    primary={menuItem.text}
                                    sx={isSelected ? selectedListItemTextStyles : listItemTextStyles}
                                    className="nestedMenuList"
                                />
                                {hasSubItems && (
                                    <ArrowIcon open={isSelected}>
                                        <img src={ExpandMore} alt="expand more" />
                                    </ArrowIcon>
                                )}
                            </>
                        )}
                    </ListItemButton>
                </Tooltip>
                {hasSubItems && (
                    <Collapse in={isSelected} timeout={200} unmountOnExit>
                        <List component="div" disablePadding className="mainMenuList">
                            {menuItem.items.menuItem.map((subItem: any, i: number) => (
                                <ListItem
                                    key={`subitem-${i}`}
                                    className={subItemClassName}
                                    onClick={() => onNavigate(subItem.navigateViewUrl)}
                                    onMouseDown={(e) => {
                                        if (e.button === 1) {
                                            // Middle click
                                            e.preventDefault();
                                            window.open(subItem.navigateViewUrl, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                    sx={subListItemStyles}
                                >
                                    <Tooltip title={subItem.text} placement="right">
                                        <ListItemIcon>{renderIcon(subItem.iconName)}</ListItemIcon>
                                    </Tooltip>
                                    {<ListItemText primary={subItem.text} />}
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                )}
            </List>
        );
    },
    (prevProps, nextProps) => {
        // Custom comparison function to prevent unnecessary re-renders
        return (
            prevProps.index === nextProps.index &&
            prevProps.selectedIndex === nextProps.selectedIndex &&
            prevProps.isOpen === nextProps.isOpen &&
            prevProps.menuItem === nextProps.menuItem
        );
    }
);

export function SCUIMainMenu({ menuContent, isOpen = false }: IProps) {
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const menuContentStore = useSelector((state: any) => state?.menu?.leftMenu);
    const [menuList, setMenuList] = useState<any>([]);
    const history = useHistory();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [menuOpen, setMenuOpen] = useState(() => {
        const savedState = localStorage.getItem('menuOpen');
        return savedState ? JSON.parse(savedState) : isOpen;
    });

    useEffect(() => {
        localStorage.setItem('menuOpen', JSON.stringify(menuOpen));
    }, [menuOpen]);

    // Optimize menu processing with useCallback
    const processMenuItems = useCallback((foundMenuItems: any[]) => {
        if (!Array.isArray(foundMenuItems)) return [];

        return foundMenuItems
            .filter((menuItem) => !menuItem.isHidden)
            .map((menuItem) => ({
                ...menuItem,
                items: {
                    ...menuItem.items,
                    menuItem: menuItem.items?.menuItem?.filter((subItem: any) => !subItem.isHidden) || [],
                },
            }))
            .filter((menuItem) => !menuItem.items?.menuItem || menuItem.items.menuItem.length > 0);
    }, []);

    // Update URLs in localStorage safely
    const updateNavigateUrls = useCallback((newUrls: string[]) => {
        try {
            localStorage.removeItem('navigateUrls');
            localStorage.setItem('navigateUrls', JSON.stringify(newUrls));
        } catch (error) {
            console.warn('Failed to update navigateUrls:', error);
        }
    }, []);

    useEffect(() => {
        let foundMenuItems =
            menuContent?.menuItem ||
            menuContentStore?.menuItem ||
            menuContentStore?.menuContent?.menuItem ||
            menuContentStore?.leftMenu?.menuContent?.menuItem ||
            menuContentStore?.leftMenu?.menuItem ||
            menuContentStore?.data?.menuItem;

        if (foundMenuItems) {
            const newUrls = new Set(['/home']);

            // Process URLs only when menu items change
            foundMenuItems.forEach((item: any) => {
                if (item.navigateViewUrl?.trim()) {
                    newUrls.add(item.navigateViewUrl);
                }

                item.items?.menuItem?.forEach((node: any) => {
                    if (node.navigateViewUrl?.trim()) {
                        newUrls.add(node.navigateViewUrl);
                    }
                });
            });

            // Add special URLs
            if (newUrls.has('/sitemanagement/authorization-rule-management')) {
                newUrls.add('/sitemanagement/edit-user');
                newUrls.add('/sitemanagement/authorization-manage');
            }
            if (newUrls.has('/returnList')) {
                newUrls.add('/returnDetail');
                newUrls.add('/refund');
                newUrls.add('/refundV2');
                newUrls.add('/createReturn');
            }
            if (newUrls.has('/productSearch')) {
                newUrls.add('/productDetail/:optionId');
            }
            if (newUrls.has('/emailTemplateBuilder')) {
                newUrls.add('/emailTemplateBuilder/templateGallery');
                newUrls.add('/emailTemplateBuilder/createTemplate');
                newUrls.add('/emailTemplateBuilder/sectionGallery');
                newUrls.add('/emailTemplateBuilder/addSection');
                newUrls.add('/emailTemplateBuilder/send-test-mail/:templateKey');
            }
            if (newUrls.has('/studio/ai-generation') || newUrls.has('/studio/ai-generation/generate-card')) {
                newUrls.add('/studio/ai-generation/history');
                newUrls.add('/studio/ai-generation/model');
                newUrls.add('/studio/ai-generation/generate-card');
                newUrls.add('/studio/ai-generation');
            }
            if (
                newUrls.has('/studio/bulk-image-upload')
                || newUrls.has('/studio/raw-image-frame-control')
                || newUrls.has('/studio/batch-output-control')
                || newUrls.has('/studio/batch-process-tracking')
            ) {
                newUrls.add('/studio/barcode-history');
            }
            if (newUrls.has('/studio/ai-studio-analytics') || newUrls.has('/studio/product-generation-report')) {
                newUrls.add('/studio/ai-studio-analytics');
                newUrls.add('/studio/product-generation-report');
            }
            if (newUrls.has('/storeCustomerManagement')) {
                newUrls.add('/store-customer-management');
            }

            // Update localStorage with new URLs
            const urlArray = Array.from(newUrls);
            updateNavigateUrls(urlArray);

            // Process menu items
            const visibleMenuItems = processMenuItems(foundMenuItems);
            setMenuList(visibleMenuItems);
        }
    }, [menuContentStore, menuContent, processMenuItems, updateNavigateUrls]);

    const _handleClickDrawerListMenu = useCallback((index: number) => {
        setSelectedIndex((prev) => (prev === index ? -1 : index));
    }, []);

    const handleNavigation = useCallback(
        (url: string) => {
            if (url) {
                history.push(url);
            }
        },
        [history]
    );

    // Memoize the menu list rendering
    const renderedMenuList = useMemo(() => {
        if (!Array.isArray(menuList) || menuList.length === 0) {
            return null;
        }
        return menuList.map((menuItem: any, index: number) => (
            <MenuItem
                key={`menu-${index}`}
                menuItem={menuItem}
                index={index}
                selectedIndex={selectedIndex}
                isOpen={!!menuOpen}
                onItemClick={_handleClickDrawerListMenu}
                onNavigate={handleNavigation}
            />
        ));
    }, [menuList, selectedIndex, menuOpen, _handleClickDrawerListMenu, handleNavigation]);

    if (!renderedMenuList) {
        return null;
    }

    return (
        <div id="mainMenuList" style={mainMenuListStyle}>
            {renderedMenuList}
        </div>
    );
}
